import React, { useEffect, useMemo, useState } from 'react';
import { Trip, Booking } from '../../types';
import apiService from '../../services/api';
import { DateDisplay } from '../Forms';

interface TripPassengerManifestProps {
  trip: Trip;
  bookings: Booking[];
}

interface VendorInfo {
  name?: string;
  companyName?: string;
  logo?: string;
}

const MANIFEST_COMPANY_LOGO = '/logo3.png';

const getApiBaseUrl = () => {
  const apiUrl = process.env.REACT_APP_API_URL || 'https://api.hajksa.com/api';
  return apiUrl.replace(/\/api\/?$/, '');
};

const getLogoUrl = (logoPath: string | null | undefined) => {
  if (!logoPath) return null;
  if (logoPath.startsWith('http')) return logoPath;
  return `${getApiBaseUrl()}${logoPath.startsWith('/') ? logoPath : `/${logoPath}`}`;
};

const TripPassengerManifest: React.FC<TripPassengerManifestProps> = ({ trip, bookings }) => {
  const [vendorInfo, setVendorInfo] = useState<VendorInfo>({});
  const [logoSrc, setLogoSrc] = useState(MANIFEST_COMPANY_LOGO);

  useEffect(() => {
    const fetchVendorData = async () => {
      try {
        const vendorData = await apiService.getVendorProfile();
        setVendorInfo({
          name: vendorData?.name,
          companyName: vendorData?.companyName,
          logo: vendorData?.logo
        });
      } catch (error) {
        setVendorInfo({});
      }
    };
    fetchVendorData();
  }, []);

  useEffect(() => {
    const vendorLogo = getLogoUrl(vendorInfo.logo);
    setLogoSrc(vendorLogo || MANIFEST_COMPANY_LOGO);
  }, [vendorInfo.logo]);

  // Collect all passengers from bookings
  const allPassengers = bookings.flatMap(booking => {
    const passengers = [];
    passengers.push({
      bookingNumber: booking.bookingNumber,
      name: booking.contactPerson.name,
      idNumber: booking.contactPerson.idNumber,
      nationality: booking.contactPerson.nationality,
      phone: booking.contactPerson.phone,
      gender: booking.contactPerson.gender,
      isMainContact: true,
      tripType: booking.tripType,
      assignedBus: booking.assignedBus?.busNumber || null
    });
    if (booking.companions && booking.companions.length > 0) {
      booking.companions.forEach(companion => {
        passengers.push({
          name: companion.name,
          idNumber: companion.idNumber,
          phone: companion.phone || '',
          gender: companion.gender,
          isMainContact: false,
          tripType: booking.tripType,
          assignedBus: booking.assignedBus?.busNumber || null
        });
      });
    }
    return passengers;
  });

  // Group passengers by bus number if buses exist
  let busPassengerMap: Record<string, typeof allPassengers> = {};
  let unassignedPassengers: typeof allPassengers = [];
  if (trip.buses && trip.buses.length > 0) {
    trip.buses.forEach(busObj => {
      const busNumber = busObj.bus.busNumber;
      busPassengerMap[busNumber] = allPassengers.filter(p => p.assignedBus === busNumber);
    });
    // Passengers not assigned to any bus
    unassignedPassengers = allPassengers.filter(p => !p.assignedBus || !trip.buses.some(b => b.bus.busNumber === p.assignedBus));
  }

  // If no buses or no assignedBus, show all in one table
  const showSingleTable = !trip.buses || trip.buses.length === 0;
  const printDate = useMemo(() => new Date(), []);

  return (
    <>
      <style>{`
        @media print {
          body { margin: 0 !important; padding: 0 !important; }
          @page { margin: 10mm !important; size: A4; }
          .manifest-container { padding: 0 !important; margin: 0 !important; box-shadow: none !important; }
          .no-print { display: none !important; }
          .manifest-logo { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
        }
      `}</style>
      <div className="manifest-container bg-white p-8 shadow-lg" style={{ fontFamily: 'Arial, sans-serif', direction: 'rtl' }}>
        {/* Header */}
        <div className="border-b-2 border-blue-600 pb-4 mb-6 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <img
              src={logoSrc}
              alt="شعار الشركة"
              className="manifest-logo w-20 h-20 object-contain"
              onError={() => setLogoSrc(MANIFEST_COMPANY_LOGO)}
            />
            <div>
              <div className="text-2xl font-bold text-blue-600 mb-1">كشف مبدئي للمسافرين</div>
              <div className="text-lg font-bold text-blue-700">{vendorInfo.companyName || 'اسم الشركة'}</div>
            </div>
          </div>
          <div className="shrink-0 text-sm">
            <div className="flex flex-col gap-2">
              <div className="flex items-center justify-end gap-4">
                <span className="text-gray-500 w-[5.5rem] text-end shrink-0">رقم الرحلة</span>
                <span className="font-semibold text-gray-900 min-w-[6.5rem] text-end">
                  {trip.tripNumber}
                </span>
              </div>
              <div className="flex items-start justify-end gap-4">
                <span className="text-gray-500 w-[5.5rem] text-end shrink-0 pt-px">تاريخ الطباعة</span>
                <DateDisplay
                  value={printDate}
                  className="items-end min-w-[6.5rem]"
                  gregorianClassName="text-sm font-semibold text-gray-900 leading-tight"
                  hijriClassName="text-xs text-gray-500 leading-tight"
                />
              </div>
            </div>
          </div>
        </div>

        {/* Passengers Table(s) */}
        {showSingleTable ? (
          <div className="mb-8">
            <h2 className="text-lg font-bold text-blue-600 mb-3">قائمة المسافرين</h2>
            <div className="overflow-x-auto">
              <table className="w-full border border-gray-300 text-sm">
                <thead className="bg-blue-600 text-white">
                  <tr>
                    <th className="border border-gray-300 p-3 text-center">#</th>
                    <th className="border border-gray-300 p-3">الاسم</th>
                    <th className="border border-gray-300 p-3">رقم الهوية</th>
                    <th className="border border-gray-300 p-3">الجنسية</th>
                    <th className="border border-gray-300 p-3">الجنس</th>
                    <th className="border border-gray-300 p-3">الهاتف</th>
                    <th className="border border-gray-300 p-3">نوع الرحلة</th>
                    <th className="border border-gray-300 p-3">التوقيع</th>
                  </tr>
                </thead>
                <tbody>
                  {allPassengers.map((passenger, index) => (
                    <tr key={index} className={passenger.isMainContact ? 'bg-blue-50' : 'bg-white'}>
                      <td className="border border-gray-300 p-3 text-center font-medium">{index + 1}</td>
                      <td className="border border-gray-300 p-3 font-medium">{passenger.name}</td>
                      <td className="border border-gray-300 p-3">{passenger.idNumber}</td>
                      <td className="border border-gray-300 p-3">{passenger.nationality}</td>
                      <td className="border border-gray-300 p-3 text-center">{passenger.gender === 'male' ? 'ذكر' : 'أنثى'}</td>
                      <td className="border border-gray-300 p-3">{passenger.phone}</td>
                      <td className="border border-gray-300 p-3 text-center">{passenger.tripType === 'hajj' ? 'حج' : passenger.tripType === 'umrah' ? 'عمرة' : 'نقل'}</td>
                      <td className="border border-gray-300 p-3 text-center"><div className="h-8 border-b border-gray-400 w-full"></div></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        ) : (
          <>
            {trip.buses.map((busObj, idx) => (
              <div className="mb-8" key={busObj.bus.busNumber}>
                <h2 className="text-lg font-bold text-blue-600 mb-3">قائمة المسافرين - الحافلة {busObj.bus.busNumber}</h2>
                <div className="overflow-x-auto">
                  <table className="w-full border border-gray-300 text-sm">
                    <thead className="bg-blue-600 text-white">
                      <tr>
                        <th className="border border-gray-300 p-3 text-center">#</th>
                        <th className="border border-gray-300 p-3">الاسم</th>
                        <th className="border border-gray-300 p-3">رقم الهوية</th>
                        <th className="border border-gray-300 p-3">الجنسية</th>
                        <th className="border border-gray-300 p-3">الجنس</th>
                        <th className="border border-gray-300 p-3">الهاتف</th>
                        <th className="border border-gray-300 p-3">نوع الرحلة</th>
                        <th className="border border-gray-300 p-3">التوقيع</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(busPassengerMap[busObj.bus.busNumber] || []).map((passenger, index) => (
                        <tr key={index} className={passenger.isMainContact ? 'bg-blue-50' : 'bg-white'}>
                          <td className="border border-gray-300 p-3 text-center font-medium">{index + 1}</td>
                          <td className="border border-gray-300 p-3 font-medium">{passenger.name}</td>
                          <td className="border border-gray-300 p-3">{passenger.idNumber}</td>
                          <td className="border border-gray-300 p-3">{passenger.nationality}</td>
                          <td className="border border-gray-300 p-3 text-center">{passenger.gender === 'male' ? 'ذكر' : 'أنثى'}</td>
                          <td className="border border-gray-300 p-3">{passenger.phone}</td>
                          <td className="border border-gray-300 p-3 text-center">{passenger.tripType === 'hajj' ? 'حج' : passenger.tripType === 'umrah' ? 'عمرة' : 'نقل'}</td>
                          <td className="border border-gray-300 p-3 text-center"><div className="h-8 border-b border-gray-400 w-full"></div></td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            ))}
            {unassignedPassengers.length > 0 && (
              <div className="mb-8">
                <h2 className="text-lg font-bold text-blue-600 mb-3">قائمة المسافرين - بدون حافلة</h2>
                <div className="overflow-x-auto">
                  <table className="w-full border border-gray-300 text-sm">
                    <thead className="bg-blue-600 text-white">
                      <tr>
                        <th className="border border-gray-300 p-3 text-center">#</th>
                        <th className="border border-gray-300 p-3">الاسم</th>
                        <th className="border border-gray-300 p-3">رقم الهوية</th>
                        <th className="border border-gray-300 p-3">الجنسية</th>
                        <th className="border border-gray-300 p-3">الجنس</th>
                        <th className="border border-gray-300 p-3">الهاتف</th>
                        <th className="border border-gray-300 p-3">نوع الرحلة</th>
                        <th className="border border-gray-300 p-3">التوقيع</th>
                      </tr>
                    </thead>
                    <tbody>
                      {unassignedPassengers.map((passenger, index) => (
                        <tr key={index} className={passenger.isMainContact ? 'bg-blue-50' : 'bg-white'}>
                          <td className="border border-gray-300 p-3 text-center font-medium">{index + 1}</td>
                          <td className="border border-gray-300 p-3 font-medium">{passenger.name}</td>
                          <td className="border border-gray-300 p-3">{passenger.idNumber}</td>
                          <td className="border border-gray-300 p-3">{passenger.nationality}</td>
                          <td className="border border-gray-300 p-3 text-center">{passenger.gender === 'male' ? 'ذكر' : 'أنثى'}</td>
                          <td className="border border-gray-300 p-3">{passenger.phone}</td>
                          <td className="border border-gray-300 p-3 text-center">{passenger.tripType === 'hajj' ? 'حج' : passenger.tripType === 'umrah' ? 'عمرة' : 'نقل'}</td>
                          <td className="border border-gray-300 p-3 text-center"><div className="h-8 border-b border-gray-400 w-full"></div></td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </>
  );
};

export default TripPassengerManifest; 